// src/pages/BookingSuccess.jsx
import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import emailjs from "@emailjs/browser";

import NavBar from "../components/common/navBar";
import Footer from "../components/common/footer";
import INFO from "../data/user";
import { API_BASE, createZoomMeeting, makeCalendarInviteUrl } from "../data/bookingLinks";

import "./styles/events.css";
import "./styles/eventSuccess.css";

const EMAILJS_SERVICE_ID = "service_jpezr0s";
const EMAILJS_TEMPLATE_ID = "template_h6tfu65";
const EMAILJS_PUBLIC_KEY = "Qc73pj33bbA0EKVxQ";
const POLL_INTERVAL_MS = 2500;
const MAX_POLLS = 48; // ~2 minutes

const BookingSuccess = () => {
	const [searchParams] = useSearchParams();
	const bookingId = searchParams.get("bookingId");

	const [status, setStatus] = useState("checking");
	const [booking, setBooking] = useState(null);
	const [downloaded, setDownloaded] = useState(false);
	const [rescheduleUrl, setRescheduleUrl] = useState("");
	const [calendarUrl, setCalendarUrl] = useState("");
	const [meetingUrl, setMeetingUrl] = useState("");
	const [meetingSetupError, setMeetingSetupError] = useState(false);
	const [confirmationEmailError, setConfirmationEmailError] = useState(false);
	const pollsRef = useRef(0);
	const timeoutRef = useRef(null);
	const emailAttemptedRef = useRef(false);

	useEffect(() => { window.scrollTo(0, 0); }, []);

	useEffect(() => {
		if (!bookingId) {
			setStatus("error");
			return undefined;
		}

		let cancelled = false;

		const poll = async () => {
			try {
				const res = await fetch(`${API_BASE}/get-order-status?bookingId=${bookingId}`);
				if (res.status === 404) {
					if (!cancelled) setStatus("error");
					return;
				}
				const data = await res.json();
				if (cancelled) return;

				if (data.status === "paid") {
					setBooking(data);
					setStatus("paid");
					return;
				}
				if (data.status === "failed") {
					sessionStorage.removeItem(`booking-reschedule:${bookingId}`);
					setStatus("failed");
					return;
				}

				pollsRef.current += 1;
				setStatus("pending");
				if (pollsRef.current < MAX_POLLS) {
					timeoutRef.current = setTimeout(poll, POLL_INTERVAL_MS);
				} else {
					setStatus("timeout");
				}
			} catch (e) {
				if (!cancelled) setStatus("error");
			}
		};

		poll();

		return () => {
			cancelled = true;
			if (timeoutRef.current) clearTimeout(timeoutRef.current);
		};
	}, [bookingId]);

	useEffect(() => {
		if (status !== "paid" || !booking || !bookingId || emailAttemptedRef.current) return;
		emailAttemptedRef.current = true;
		let customer;
		try {
			customer = JSON.parse(sessionStorage.getItem(`booking-reschedule:${bookingId}`) || "null");
		} catch (error) {
			console.error("Could not read booking confirmation details:", error);
			return;
		}
		if (!customer?.token || !customer.email) return;

		const url = new URL("/booking/reschedule", window.location.origin);
		url.searchParams.set("token", customer.token);
		setRescheduleUrl(url.toString());
		const inviteUrl = makeCalendarInviteUrl(bookingId, customer.token);
		setCalendarUrl(inviteUrl);

		const sendConfirmation = async () => {
			let zoomUrl = "";
			if (booking.mode === "virtual") {
				try {
					zoomUrl = await createZoomMeeting(bookingId, customer.token);
					setMeetingUrl(zoomUrl);
				} catch (error) {
					console.error("Zoom meeting setup failed:", error);
					setMeetingSetupError(true);
				}
			}

			const bookingDate = new Date(`${booking.date}T00:00:00`).toLocaleDateString("en-ZA", {
				weekday: "long", day: "numeric", month: "long", year: "numeric",
			});
			const confirmationLinks = [
				`Add this appointment to your calendar: ${inviteUrl}`,
				zoomUrl ? `Join your virtual appointment on Zoom: ${zoomUrl}` : "",
			].filter(Boolean).join("\n");
			try {
				await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, {
					to_name: customer.name,
					to_email: customer.email,
					email_subject: "Booking confirmed",
					email_title: "Booking Confirmed",
					email_intro: "Your booking is confirmed. Here's a summary of your appointment:",
					report_body: confirmationLinks,
					action_label: "Reschedule appointment",
					action_url: url.toString(),
					service_name: booking.serviceTitle,
					mode: booking.mode === "in-person" ? "In Person" : "Virtual",
					booking_date: bookingDate,
					booking_time: booking.time,
					duration: booking.duration ? `${booking.duration} min` : "",
					calendar_url: inviteUrl,
					meeting_url: zoomUrl,
					reschedule_url: url.toString(),
				}, EMAILJS_PUBLIC_KEY);
				if (booking.mode !== "virtual" || zoomUrl) {
					sessionStorage.removeItem(`booking-reschedule:${bookingId}`);
				}
			} catch (error) {
				console.error("Booking confirmation email failed:", error);
				setConfirmationEmailError(true);
			}
		};
		sendConfirmation();
	}, [booking, bookingId, status]);

	const retry = () => window.location.reload();

	return (
		<>
			<Helmet>
				<title>{`Booking Confirmed | ${INFO.main.title}`}</title>
			</Helmet>

			<div className="page-content">
				<NavBar active="booking" />

				<div className="content-wrapper">
					<section className="ev-page es-page">
						<div className="ev-container es-container">
							{status === "checking" && (
								<div className="es-status">
									<div className="es-status__spinner" aria-hidden="true" />
									<h2>Confirming your payment…</h2>
									<p>Just a moment while we check with Yoco.</p>
								</div>
							)}

							{status === "pending" && (
								<div className="es-status">
									<div className="es-status__spinner" aria-hidden="true" />
									<h2>Payment received</h2>
									<p>Confirming your booking now — this only takes a few seconds.</p>
								</div>
							)}

							{status === "paid" && booking && (
								<div className="es-wrap">
									<div className="es-badge">
										<svg viewBox="0 0 52 52" className="es-badge__check">
											<circle cx="26" cy="26" r="25" className="es-badge__circle" />
											<path d="M14 27l8 8 16-16" className="es-badge__tick" />
										</svg>
									</div>

									<h1 className="es-title">You're booked in! 🎉</h1>
									<p className="es-subtitle">Your receipt is ready to download below.</p>
									{confirmationEmailError && (
										<p role="status">Your booking is confirmed, but we couldn’t send the confirmation email. You can still manage your appointment below.</p>
									)}
									{meetingSetupError && (
										<p role="status">Your booking is confirmed, but we couldn’t create its Zoom link yet. Please refresh this page shortly or contact us before your appointment.</p>
									)}

									<div className="es-ticket">
										<div className="es-ticket__top">
											<span className="es-ticket__eyebrow">Booking confirmed</span>
											<h2 className="es-ticket__event">{booking.serviceTitle}</h2>
											<div className="es-ticket__meta">
												<span className="es-ticket__pill">{booking.date}</span>
												<span className="es-ticket__pill">{booking.time}</span>
											</div>
										</div>

										<div className="es-perf" aria-hidden="true">
											<span className="es-perf__notch es-perf__notch--left" />
											<span className="es-perf__notch es-perf__notch--right" />
										</div>

										<div className="es-ticket__bottom">
											<a
												className={`ev-btn es-download-btn${downloaded ? " es-download-btn--done" : ""}`}
												href={`${API_BASE}/booking-receipt?bookingId=${bookingId}`}
												onClick={() => setDownloaded(true)}
											>
												{downloaded ? <>✓ Downloaded — get it again</> : <>⬇ Download your receipt (PDF)</>}
											</a>
												{calendarUrl && (
													<a className="ev-btn ev-btn--ghost es-reschedule-btn" href={calendarUrl}>
														Add to calendar (.ics)
													</a>
												)}
												{meetingUrl && (
													<a
														className="ev-btn ev-btn--ghost es-reschedule-btn"
														href={meetingUrl}
														target="_blank"
														rel="noreferrer"
													>
														Join your Zoom meeting
													</a>
												)}
												{rescheduleUrl && (
												<a className="ev-btn ev-btn--ghost es-reschedule-btn" href={rescheduleUrl}>
													Reschedule your appointment
												</a>
											)}
										</div>
									</div>

									<p className="es-footnote">
										You can return to this page any time to re-download your receipt.
									</p>
								</div>
							)}

							{status === "failed" && (
								<div className="es-status es-status--error">
									<div className="es-status__icon">✕</div>
									<h2>Payment didn't go through</h2>
									<p>Your booking was not confirmed. Please try again.</p>
									<Link className="ev-btn ev-btn--ghost" to="/booking">Back to booking</Link>
								</div>
							)}

							{status === "timeout" && (
								<div className="es-status">
									<div className="es-status__icon">⏱</div>
									<h2>Still confirming</h2>
									<p>This is taking longer than usual — your payment provider may be slow to respond.</p>
									<button className="ev-btn ev-btn--ghost" onClick={retry}>Check again</button>
								</div>
							)}

							{status === "error" && (
								<div className="es-status es-status--error">
									<div className="es-status__icon">?</div>
									<h2>We couldn't find this booking</h2>
									<p>If you were charged, please contact us with your payment reference.</p>
									<Link className="ev-btn ev-btn--ghost" to="/booking">Back to booking</Link>
								</div>
							)}
						</div>
					</section>

					<div className="page-footer">
						<Footer />
					</div>
				</div>
			</div>
		</>
	);
};

export default BookingSuccess;